import { Constants } from '@pwc/pwc-deals-web-common';
import _get from 'lodash/get';
import _isFinite from 'lodash/isFinite';
import { createSelector } from 'reselect';

import { sumIfNumber } from '../utilities/calculationUtilities';
import {
  ALL_ADJUSTMENTS_KEY,
  FACT_CALCULATIONS_KEY,
  REPORTED_EBITDA_KEY,
  TIME_PERIODS_BY_CODE_KEY,
  TOTALS_KEY,
} from '../utilities/constants';
import {
  getAdjustmentSumsByCode,
  getRevenueAdjustmentsForQofE,
} from './adjustmentSelectors';
import {
  getReportedEbitda,
  getReportedRevenue,
} from './factCalculationsSelectors';
import { getTimePeriodsForQofE } from './timePeriodSelectors';

const calculateFinancialSumsForTimePeriods = (
  sums,
  adjustmentTotals,
  timePeriods
) =>
  timePeriods.reduce((acc, currentTimePeriod) => {
    const sumForCurrent = sumIfNumber(
      adjustmentTotals[currentTimePeriod],
      sums[currentTimePeriod]
    );
    return {
      ...acc,
      [currentTimePeriod]: sumForCurrent,
    };
  }, {});

const reportedEBITDASelector = (state) =>
  _get(state, `${FACT_CALCULATIONS_KEY}.${REPORTED_EBITDA_KEY}`, {});

const qofeAllAdjustmentTotalSelector = createSelector(
  getAdjustmentSumsByCode,
  (adjustmentSums) => {
    return _get(
      adjustmentSums,
      [Constants.QUALITY_OF_EARNINGS, ALL_ADJUSTMENTS_KEY, TOTALS_KEY],
      {}
    );
  }
);

const qofeTimePeriodsSelector = (state) =>
  _get(
    state,
    `${TIME_PERIODS_BY_CODE_KEY}.${Constants.QUALITY_OF_EARNINGS}`,
    []
  );

export const getAdjustedEBITDA = createSelector(
  reportedEBITDASelector,
  qofeAllAdjustmentTotalSelector,
  qofeTimePeriodsSelector,
  (reportedEBITDA, qofeAllAdjustmentTotals, qofeTimePeriods) =>
    calculateFinancialSumsForTimePeriods(
      reportedEBITDA,
      qofeAllAdjustmentTotals,
      qofeTimePeriods
    )
);

export const getAdjustedRevenue = createSelector(
  getTimePeriodsForQofE,
  getReportedRevenue,
  getRevenueAdjustmentsForQofE,

  (qofeTimePeriods, reportedRevenue, qofeRevenueAdjustments) =>
    qofeTimePeriods.reduce((acc, qofeTimePeriod) => {
      let revenueAdjustmentForPeriod = _get(
        qofeRevenueAdjustments,
        qofeTimePeriod,
        0
      );
      let revenueForPeriod = _get(reportedRevenue, qofeTimePeriod, 0);

      revenueAdjustmentForPeriod = _isFinite(revenueAdjustmentForPeriod)
        ? revenueAdjustmentForPeriod
        : 0;
      revenueForPeriod = _isFinite(revenueForPeriod) ? revenueForPeriod : 0;

      const adjustedRevenueForPeriod =
        revenueAdjustmentForPeriod + revenueForPeriod;

      return {
        ...acc,
        [qofeTimePeriod]: adjustedRevenueForPeriod,
      };
    }, {})
);

export const getPercentOfRevenue = createSelector(
  getTimePeriodsForQofE,
  getReportedRevenue,
  getReportedEbitda,
  (timePeriods, reportedRevenue, reportedEbitda) => {
    return timePeriods.reduce((acc, currentTimePeriod) => {
      const currentRevenue = reportedRevenue[currentTimePeriod];
      const currentEbitda = reportedEbitda[currentTimePeriod];
      const percentOfRevenue =
        parseFloat(currentEbitda) / parseFloat(currentRevenue);
      const formattedPercentOfRevenue = _isFinite(percentOfRevenue)
        ? percentOfRevenue
        : undefined;
      return { ...acc, [currentTimePeriod]: formattedPercentOfRevenue };
    }, {});
  }
);

export const getPercentOfAdjustedRevenue = createSelector(
  getTimePeriodsForQofE,
  getAdjustedRevenue,
  getAdjustedEBITDA,
  (timePeriods, adjustedRevenue, adjustedEbitda) => {
    return timePeriods.reduce((acc, currentTimePeriod) => {
      const currentRevenue = adjustedRevenue[currentTimePeriod];
      const currentEbitda = adjustedEbitda[currentTimePeriod];
      const percentOfRevenue =
        parseFloat(currentEbitda) / parseFloat(currentRevenue);
      const formattedPercentOfRevenue = _isFinite(percentOfRevenue)
        ? percentOfRevenue
        : undefined;
      return { ...acc, [currentTimePeriod]: formattedPercentOfRevenue };
    }, {});
  }
);
